
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import type { Application, CorporateStructure } from '@/types/database.types';
import { normalizeFormData } from '@/hooks/application/save-utils';

// Import components
import { CompanyInformation } from './CompanyInformation';
import { EntityStatus } from './EntityStatus';

interface EntityDetailsProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
  readOnly?: boolean;
}

export const EntityDetails = ({ data, onSave, readOnly = false }: EntityDetailsProps) => {
  const { toast } = useToast();
  
  // Make sure to normalize the corporate_structure properly
  const normalizedStructure = data.corporate_structure === null ? 
    null : data.corporate_structure as CorporateStructure | null;
  
  const { register, handleSubmit, reset, watch, setValue } = useForm<Partial<Application>>({
    defaultValues: {
      company_legal_name: data.company_legal_name || '',
      company_dba: data.company_dba || '',
      company_website: data.company_website || '',
      ein: data.ein || '',
      naics_code: data.naics_code || '',
      corporate_structure: normalizedStructure,
      is_non_profit: data.is_non_profit || false,
      current_peo: data.current_peo || false,
      current_provider: data.current_provider || '',
      current_payroll_platform: data.current_payroll_platform || '',
      year_founded: data.year_founded || '',
    }
  });

  // Reset form when data changes
  useEffect(() => {
    reset({
      company_legal_name: data.company_legal_name || '',
      company_dba: data.company_dba || '',
      company_website: data.company_website || '',
      ein: data.ein || '',
      naics_code: data.naics_code || '',
      corporate_structure: normalizedStructure,
      is_non_profit: !!data.is_non_profit,
      current_peo: !!data.current_peo,
      current_provider: data.current_provider || '',
      current_payroll_platform: data.current_payroll_platform || '',
      year_founded: data.year_founded || '',
    });
    
    // Set boolean values explicitly to ensure they're properly initialized
    setValue('is_non_profit', !!data.is_non_profit);
    setValue('current_peo', !!data.current_peo);
  }, [data, reset, setValue]);

  const onSubmit = handleSubmit((formData) => {
    try {
      console.log('Form data before save:', {
        ...formData,
        corporateStructure: formData.corporate_structure,
        isNonProfit: formData.is_non_profit,
        currentPeo: formData.current_peo,
        currentPayrollPlatform: formData.current_payroll_platform
      });
      
      // Normalize any placeholder values
      const normalizedData = normalizeFormData(formData);
      
      // Ensure boolean values are correctly set
      const dataToSave = {
        ...normalizedData,
        corporate_structure: normalizedData.corporate_structure,
        is_non_profit: !!normalizedData.is_non_profit,
        current_peo: !!normalizedData.current_peo,
        current_payroll_platform: normalizedData.current_payroll_platform
      };
      
      console.log('Data being sent to onSave:', {
        ...dataToSave,
        corporateStructure: dataToSave.corporate_structure,
        isNonProfit: dataToSave.is_non_profit,
        currentPeo: dataToSave.current_peo,
        currentPayrollPlatform: dataToSave.current_payroll_platform
      });
      
      onSave(dataToSave);
      
      // Remove the toast notification that was causing the red alert
      // toast({
      //   title: "Entity information saved",
      //   description: "Your entity information has been updated.",
      // });
    } catch (error) {
      console.error('Save error:', error);
      // Keep error toasts for actual errors
      toast({
        title: "Error saving data",
        description: "There was an error saving your entity information.",
        variant: "destructive",
      });
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <Card className="border-primary/5 shadow-elevation-1 overflow-hidden">
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
            <h2 className="text-2xl font-semibold">Entity Information</h2>
          </div>
          
          <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-start gap-3">
            <p className="text-muted-foreground text-sm">
              Accurate details about the organization's structure help us deliver faster, more tailored PEO matches—while also removing bias from the evaluation process.
            </p>
          </div>
          
          <div className="space-y-8">
            {/* Organization Information Section */}
            <div className="bg-white p-5 rounded-lg border border-gray-100 shadow-sm">
              <h3 className="text-lg font-medium mb-5 pb-2 border-b">Organization Information</h3>
              <CompanyInformation 
                register={register} 
                watch={watch} 
                setValue={setValue} 
                data={data} 
              />
              
              <div className="mt-8">
                <EntityStatus 
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  readOnly={readOnly}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};
